import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { getPostBySlug, getSortedPosts } from '@/data/blog-posts';
import { articleSchema, breadcrumbListSchema } from '@/lib/structured-data';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const post = slug ? getPostBySlug(slug) : undefined;
  
  // Redirect reports to the report page
  useEffect(() => {
    if (post?.type === 'report') {
      navigate(`/report/${slug}`, { replace: true });
    }
  }, [post, slug, navigate]);
  
  // Get related posts (exclude current post and limit to 3)
  const relatedPosts = getSortedPosts()
    .filter(p => p.slug !== slug)
    .slice(0, 3);
  
  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Helmet>
          <title>Blog Post Not Found | Fordje</title>
          <meta name="description" content="The requested blog post could not be found." />
          <meta name="robots" content="noindex, follow" />
          {/* Basic OG tags for not found page */}
          <meta property="og:title" content="Blog Post Not Found | Fordje" key="og:title" />
          <meta property="og:description" content="The requested blog post could not be found." key="og:description" />
          <meta property="og:type" content="website" key="og:type" />
          <meta property="og:url" content={`https://www.fordje.com/blog`} key="og:url" />
          <meta property="og:image" content="https://www.fordje.com/og-image.png" key="og:image" />
          <meta property="og:site_name" content="Fordje Blog" key="og:site_name" />
        </Helmet>
        <Header />
        <main className="flex-grow container-custom section-padding">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Blog Post Not Found</h1>
            <p className="mb-8">The blog post you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/blog')}>
              Back to Blog
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Article schema for the blog post
  const structuredData = articleSchema({
    type: 'BlogPosting',
    title: post.title,
    description: post.metaDescription || post.excerpt,
    slug: post.slug,
    pathPrefix: '/blog',
    coverImage: post.coverImage,
    datePublished: post.date,
    author: post.author,
    categories: post.categories,
    keywords: post.seoKeywords,
    readTimeMinutes: post.readTime,
    wordCount: post.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length,
  });

  // BreadcrumbList schema: Home > Insights > Post title
  const breadcrumbData = breadcrumbListSchema([
    { name: 'Home', path: '/' },
    { name: 'Insights', path: '/blog' },
    { name: post.title, path: `/blog/${post.slug}` },
  ]);

  // Format the date for display
  const formattedDate = new Date(post.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Generate the current page URL for OG tags
  const currentPageUrl = `https://www.fordje.com/blog/${post.slug}`;

  // SEO optimized meta descriptions and titles
  const metaTitle = `${post.title} | Fordje Blog`;
  const metaDescription = post.metaDescription || post.excerpt;
  const socialTitle = post.socialShareTitle || post.title;
  const socialDescription = post.socialShareDescription || post.excerpt;
  const keywords = post.seoKeywords ? 
    `${post.seoKeywords.join(', ')}, ${post.categories.join(', ')}, regulatory data, construction codes, building regulations` :
    `${post.categories.join(', ')}, regulatory data, construction codes, building regulations`;

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="author" content={post.author.name} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href={currentPageUrl} />
        
        {/* Enhanced Open Graph Tags */}
        <meta property="og:title" content={socialTitle} key="og:title" />
        <meta property="og:description" content={socialDescription} key="og:description" />
        <meta property="og:image" content={post.coverImage} key="og:image" />
        <meta property="og:image:alt" content={`Featured image for ${post.title}`} key="og:image:alt" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="article" key="og:type" />
        <meta property="og:url" content={currentPageUrl} key="og:url" />
        <meta property="og:site_name" content="Fordje Blog" key="og:site_name" />
        <meta property="og:locale" content="en_US" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.date} />
        <meta property="article:author" content={post.author.name} />
        <meta property="article:section" content={post.categories[0]} />
        {post.categories.map((category, index) => (
          <meta property="article:tag" content={category} key={`article:tag:${index}`} />
        ))}
        
        {/* Enhanced Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
        <meta name="twitter:title" content={socialTitle} key="twitter:title" />
        <meta name="twitter:description" content={socialDescription} key="twitter:description" />
        <meta name="twitter:image" content={post.coverImage} key="twitter:image" />
        <meta name="twitter:image:alt" content={`Featured image for ${post.title}`} />
        <meta name="twitter:site" content="@fordje" key="twitter:site" />
        <meta name="twitter:creator" content="@fordje" key="twitter:creator" />
        <meta name="twitter:label1" content="Reading time" />
        <meta name="twitter:data1" content={`${post.readTime} minutes`} />
        <meta name="twitter:label2" content="Written by" />
        <meta name="twitter:data2" content={post.author.name} />
        
        {/* Additional SEO tags */}
        <meta name="theme-color" content="#1e293b" />
        <meta name="msapplication-TileColor" content="#1e293b" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData)}
        </script>
      </Helmet>
      <Header />
      <main className="flex-grow bg-slate-50">
        <article className="container-custom section-padding" itemScope itemType="https://schema.org/BlogPosting">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 text-fordje-gray-dark hover:text-fordje-blue mb-8 transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Back to all posts
            </Link>
            
            <Card className="overflow-hidden bg-white shadow-sm p-8 mb-8">
              <div className="mb-8">
                <div className="flex gap-2 mb-4">
                  {post.categories.map((category) => (
                    <Badge key={category} variant="secondary" className="font-normal">
                      {category}
                    </Badge>
                  ))}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-6" itemProp="headline">{post.title}</h1>
                <div className="flex items-center gap-4 mb-8">
                  <Avatar>
                    <AvatarImage src={post.author.avatar} alt={`Photo of ${post.author.name}`} />
                    <AvatarFallback>{post.author.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium" itemProp="author">{post.author.name}</p>
                    <p className="text-sm text-muted-foreground">
                      <time dateTime={post.date} itemProp="datePublished">
                        {formattedDate}
                      </time> • {post.readTime} min read
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mb-8 overflow-hidden rounded-lg">
                <img 
                  src={post.coverImage} 
                  alt={`Featured image for ${post.title}`}
                  itemProp="image"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              
              <div className="prose prose-lg max-w-none" itemProp="articleBody">
                <div 
                  dangerouslySetInnerHTML={{ __html: post.content }} 
                  className="blog-content [&>ul]:list-disc [&>ol]:list-decimal [&_iframe]:w-full [&_iframe]:h-[600px] [&_iframe]:rounded-lg [&_.aspect-w-16.aspect-h-9]:relative [&_.aspect-w-16.aspect-h-9_iframe]:absolute [&_.aspect-w-16.aspect-h-9_iframe]:inset-0 [&_a]:text-fordje-blue [&_a]:font-medium [&_a]:border-b [&_a]:border-fordje-blue/30 [&_a]:transition-colors [&_a:hover]:text-fordje-blue-dark [&_a:hover]:border-fordje-blue [&_p:has(em)]:bg-fordje-blue/5 [&_p:has(em)]:border-l-4 [&_p:has(em)]:border-fordje-blue [&_p:has(em)]:p-6 [&_p:has(em)]:my-8 [&_p:has(em)]:rounded-r-lg [&_p:has(em)]:max-w-none [&_p:has(em)_em]:not-italic [&_p:has(em)_em]:font-medium [&_p:has(em)_em]:text-fordje-navy"
                />
              </div>
            </Card>
            
            {/* Related Posts Section */}
            {relatedPosts.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-6">Related Posts</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedPosts.map((relatedPost) => (
                    <Card key={relatedPost.slug} className="group hover:shadow-lg transition-all duration-200 cursor-pointer bg-white">
                      <Link to={`/blog/${relatedPost.slug}`} className="block">
                        <div className="overflow-hidden rounded-t-lg">
                          <img 
                            src={relatedPost.coverImage} 
                            alt={relatedPost.title}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        </div>
                        <div className="p-6">
                          <div className="flex gap-2 mb-3">
                            {relatedPost.categories.slice(0, 2).map((category) => (
                              <Badge key={category} variant="secondary" className="text-xs">
                                {category}
                              </Badge>
                            ))}
                          </div>
                          <h3 className="font-semibold text-lg mb-2 group-hover:text-fordje-blue transition-colors line-clamp-2">
                            {relatedPost.title}
                          </h3>
                          <p className="text-muted-foreground text-sm line-clamp-3 mb-3">
                            {relatedPost.excerpt}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(relatedPost.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })} • {relatedPost.readTime} min read
                          </p>
                        </div>
                      </Link>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            
            <Separator className="my-12" />
            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-8 rounded-lg shadow-sm">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={post.author.avatar} alt={`Photo of ${post.author.name}`} />
                  <AvatarFallback>{post.author.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-lg">{post.author.name}</p>
                  <p className="text-muted-foreground">{post.author.title}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Button variant="outline" onClick={() => navigate('/blog')}>
                  More articles
                </Button>
                <Button onClick={() => navigate('/request-demo')}>
                  Get a Demo
                </Button>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
